// components/AdvancedProjectFilterBuilder.jsx
import React, { useState, useEffect } from "react";
import { Plus, Trash2, Save, Filter } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const PROJECT_FIELDS = [
  // Basic Information
  { value: "projectName", label: "Project Name", type: "string" },
  { value: "projectNumber", label: "Project Number", type: "string" },
  { value: "description", label: "Description", type: "string" },
  { value: "projectNotes", label: "Project Notes", type: "string" },
  { value: "type", label: "Project Type", type: "string" },

  // Status and Priority
  {
    value: "status",
    label: "Status",
    type: "select",
    options: [
      { value: "Survey Requested", label: "Survey Requested" },
      { value: "Awaiting Survey", label: "Awaiting Survey" },
      { value: "Survey Booked", label: "Survey Booked" },
      { value: "Survey Completed", label: "Survey Completed" },
      { value: "Quotation", label: "Quotation" },
      { value: "Installation", label: "Installation" },
      {
        value: "Awaiting Finance Approval",
        label: "Awaiting Finance Approval",
      },
      { value: "Porting", label: "Porting" },
      { value: "Broadband Install", label: "Broadband Install" },
      { value: "Complete", label: "Complete" },
      { value: "Cancelled", label: "Cancelled" },
    ],
  },
  {
    value: "priority",
    label: "Priority",
    type: "select",
    options: [
      { value: "Low", label: "Low" },
      { value: "Medium", label: "Medium" },
      { value: "High", label: "High" },
      { value: "Urgent", label: "Urgent" },
    ],
  },

  // Numeric Fields
  { value: "daysOpen", label: "Days Open", type: "number" },

  // User/People Fields
  { value: "ownerId", label: "Owner", type: "user" },
  { value: "projectManagerId", label: "Project Manager", type: "user" },
  { value: "surveyCompletedBy", label: "Survey Completed By", type: "user" },
  { value: "createdById", label: "Created By", type: "user" },
  { value: "lastModifiedById", label: "Last Modified By", type: "user" },

  // Key Dates
  { value: "startDate", label: "Start Date", type: "date" },
  { value: "endDate", label: "End Date", type: "date" },
  { value: "createdAt", label: "Created Date", type: "date" },
  { value: "updatedAt", label: "Last Updated", type: "date" },

  // Survey Dates
  {
    value: "surveyCompletedDate",
    label: "Survey Completed Date",
    type: "date",
  },

  // Finance Dates
  {
    value: "financeCheckSubmittedDate",
    label: "Finance Check Submitted",
    type: "date",
  },

  // Broadband Dates
  {
    value: "broadbandRequestDate",
    label: "Broadband Request Date",
    type: "date",
  },
  {
    value: "broadbandInstalledDate",
    label: "Broadband Installed Date",
    type: "date",
  },

  // Installation Dates
  { value: "installDate", label: "Install Date", type: "date" },
  { value: "installationDate", label: "Installation Date", type: "date" },
  {
    value: "installationCompletedDate",
    label: "Installation Completed Date",
    type: "date",
  },
  {
    value: "installCompletedDateDEP",
    label: "Install Completed (DEP)",
    type: "date",
  },
  {
    value: "stageTwoInstallDate",
    label: "Stage Two Install Date",
    type: "date",
  },

  // Port Dates
  { value: "portRequestDate", label: "Port Request Date", type: "date" },
  { value: "portAcceptedDate", label: "Port Accepted Date", type: "date" },

  // System Dates
  { value: "systemOrderedDate", label: "System Ordered Date", type: "date" },

  // Salesforce Dates
  {
    value: "salesforceCreatedDate",
    label: "Salesforce Created Date",
    type: "date",
  },
  {
    value: "salesforceLastModifiedDate",
    label: "Salesforce Last Modified Date",
    type: "date",
  },
  {
    value: "salesforceSystemModstamp",
    label: "Salesforce System Modstamp",
    type: "date",
  },
  { value: "lastActivityDate", label: "Last Activity Date", type: "date" },
  { value: "lastReferencedDate", label: "Last Referenced Date", type: "date" },
  { value: "lastViewedDate", label: "Last Viewed Date", type: "date" },

  // Boolean Flags - Blocked Status
  { value: "projectBlocked", label: "Project Blocked", type: "boolean" },
  { value: "projectBlockedReason", label: "Blocked Reason", type: "string" },

  // Boolean Flags - Survey
  { value: "surveyFileSaved", label: "Survey File Saved", type: "boolean" },

  // Boolean Flags - Finance
  { value: "financeApproved", label: "Finance Approved", type: "boolean" },

  // Boolean Flags - Hardware/Kit
  { value: "hardwareOrdered", label: "Hardware Ordered", type: "boolean" },
  { value: "kitListCreated", label: "Kit List Created", type: "boolean" },
  {
    value: "paperworkCheckedAndSaved",
    label: "Paperwork Checked & Saved",
    type: "boolean",
  },

  // Boolean Flags - Broadband
  {
    value: "broadbandNotRequired",
    label: "Broadband Not Required",
    type: "boolean",
  },
  {
    value: "broadbandInfoUpdated",
    label: "Broadband Info Updated",
    type: "string",
  },

  // Boolean Flags - Installation
  {
    value: "installsPaperworkComplete",
    label: "Installs Paperwork Complete",
    type: "boolean",
  },

  // Boolean Flags - Service
  {
    value: "serviceActivityComplete",
    label: "Service Activity Complete",
    type: "boolean",
  },
  {
    value: "serviceCheckerComplete",
    label: "Service Checker Complete",
    type: "boolean",
  },
  { value: "systemBuilt", label: "System Built", type: "boolean" },
  { value: "techInfoCompleted", label: "Tech Info Completed", type: "boolean" },

  // Integration
  { value: "source", label: "Source", type: "string" },
  { value: "sourceId", label: "Source ID", type: "string" },
];

const AdvancedProjectFilterBuilder = ({
  onApply,
  onSave,
  initialFilters = null,
  showSaveButton = false,
  users = [],
}) => {
  const { isDarkMode } = useTheme();

  const [filterConfig, setFilterConfig] = useState({
    logic: "AND",
    conditions: [],
  });
  const [userList, setUserList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const response = await apiService.getCompanyUsers();
        if (response.success && response.users) {
          setUserList(response.users);
        }
      } catch (error) {
        console.error("Failed to fetch users:", error);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (initialFilters) {
      setFilterConfig(initialFilters);
    }
  }, [initialFilters]);

  // Shared class helpers
  const selectCls = `px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-blue-500 transition-colors ${
    isDarkMode
      ? "bg-gray-700 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const inputCls = `px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-blue-500 transition-colors ${
    isDarkMode
      ? "bg-gray-700 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const getUserOptions = () => {
    const usersToMap = userList.length > 0 ? userList : users;
    return (usersToMap || []).map((user) => ({
      value: user.id,
      label: user.name || user.email || `User ${user.id}`,
    }));
  };

  const getFieldInfo = (fieldValue) => {
    const field = PROJECT_FIELDS.find((f) => f.value === fieldValue);
    if (field && field.type === "user") {
      return { ...field, options: getUserOptions() };
    }
    return field;
  };

  const OPERATORS = {
    string: [
      { value: "equals", label: "Equals" },
      { value: "not_equals", label: "Not Equals" },
      { value: "contains", label: "Contains" },
      { value: "not_contains", label: "Does Not Contain" },
      { value: "starts_with", label: "Starts With" },
      { value: "ends_with", label: "Ends With" },
      { value: "is_empty", label: "Is Empty" },
      { value: "is_not_empty", label: "Is Not Empty" },
      { value: "in", label: "In List" },
      { value: "not_in", label: "Not In List" },
    ],
    number: [
      { value: "equals", label: "Equals" },
      { value: "not_equals", label: "Not Equals" },
      { value: "greater_than", label: "Greater Than" },
      { value: "less_than", label: "Less Than" },
      { value: "greater_than_or_equal", label: "Greater Than or Equal" },
      { value: "less_than_or_equal", label: "Less Than or Equal" },
      { value: "between", label: "Between" },
      { value: "is_empty", label: "Is Empty" },
      { value: "is_not_empty", label: "Is Not Empty" },
    ],
    decimal: [
      { value: "equals", label: "Equals" },
      { value: "not_equals", label: "Not Equals" },
      { value: "greater_than", label: "Greater Than" },
      { value: "less_than", label: "Less Than" },
      { value: "greater_than_or_equal", label: "Greater Than or Equal" },
      { value: "less_than_or_equal", label: "Less Than or Equal" },
      { value: "between", label: "Between" },
      { value: "is_empty", label: "Is Empty" },
      { value: "is_not_empty", label: "Is Not Empty" },
    ],
    date: [
      { value: "equals", label: "Equals" },
      { value: "not_equals", label: "Not Equals" },
      { value: "before", label: "Before" },
      { value: "after", label: "After" },
      { value: "between", label: "Between" },
      { value: "in_last", label: "In Last X Days" },
      { value: "in_next", label: "In Next X Days" },
      { value: "is_empty", label: "Is Empty" },
      { value: "is_not_empty", label: "Is Not Empty" },
    ],
    boolean: [
      { value: "is_true", label: "Is True" },
      { value: "is_false", label: "Is False" },
    ],
    select: [
      { value: "equals", label: "Equals" },
      { value: "not_equals", label: "Not Equals" },
      { value: "in", label: "In List" },
      { value: "not_in", label: "Not In List" },
      { value: "is_empty", label: "Is Empty" },
      { value: "is_not_empty", label: "Is Not Empty" },
    ],
    user: [
      { value: "equals", label: "Equals" },
      { value: "not_equals", label: "Not Equals" },
      { value: "in", label: "In List" },
      { value: "not_in", label: "Not In List" },
      { value: "is_empty", label: "Is Empty" },
      { value: "is_not_empty", label: "Is Not Empty" },
    ],
  };

  const addCondition = (groupIndex = null) => {
    const newCondition = {
      id: Date.now().toString(),
      field: "",
      operator: "",
      value: "",
      dataType: "string",
    };
    if (groupIndex !== null) {
      const newConfig = { ...filterConfig };
      newConfig.conditions[groupIndex].conditions.push(newCondition);
      setFilterConfig(newConfig);
    } else {
      setFilterConfig({
        ...filterConfig,
        conditions: [...filterConfig.conditions, newCondition],
      });
    }
  };

  const addConditionGroup = () => {
    const newGroup = {
      id: Date.now().toString(),
      type: "group",
      logic: "AND",
      conditions: [],
    };
    setFilterConfig({
      ...filterConfig,
      conditions: [...filterConfig.conditions, newGroup],
    });
  };

  const removeCondition = (conditionId, groupIndex = null) => {
    if (groupIndex !== null) {
      const newConfig = { ...filterConfig };
      newConfig.conditions[groupIndex].conditions = newConfig.conditions[
        groupIndex
      ].conditions.filter((c) => c.id !== conditionId);
      setFilterConfig(newConfig);
    } else {
      setFilterConfig({
        ...filterConfig,
        conditions: filterConfig.conditions.filter((c) => c.id !== conditionId),
      });
    }
  };

  const updateCondition = (conditionId, updates, groupIndex = null) => {
    if (groupIndex !== null) {
      const newConfig = { ...filterConfig };
      const conditionIndex = newConfig.conditions[
        groupIndex
      ].conditions.findIndex((c) => c.id === conditionId);
      if (conditionIndex !== -1) {
        newConfig.conditions[groupIndex].conditions[conditionIndex] = {
          ...newConfig.conditions[groupIndex].conditions[conditionIndex],
          ...updates,
        };
        if (updates.field) {
          const fieldInfo = getFieldInfo(updates.field);
          newConfig.conditions[groupIndex].conditions[conditionIndex].operator =
            "";
          newConfig.conditions[groupIndex].conditions[conditionIndex].value =
            "";
          newConfig.conditions[groupIndex].conditions[conditionIndex].dataType =
            fieldInfo?.type || "string";
        }
        if (updates.operator) {
          newConfig.conditions[groupIndex].conditions[conditionIndex].value =
            "";
        }
        setFilterConfig(newConfig);
      }
    } else {
      const newConditions = filterConfig.conditions.map((condition) => {
        if (condition.id === conditionId) {
          const updated = { ...condition, ...updates };
          if (updates.field) {
            const fieldInfo = getFieldInfo(updates.field);
            updated.operator = "";
            updated.value = "";
            updated.dataType = fieldInfo?.type || "string";
          }
          if (updates.operator) {
            updated.value = "";
          }
          return updated;
        }
        return condition;
      });
      setFilterConfig({ ...filterConfig, conditions: newConditions });
    }
  };

  const updateGroupLogic = (groupIndex, logic) => {
    const newConfig = { ...filterConfig };
    newConfig.conditions[groupIndex].logic = logic;
    setFilterConfig(newConfig);
  };

  const updateRootLogic = (logic) => {
    setFilterConfig({ ...filterConfig, logic });
  };

  const handleApply = () => {
    if (onApply) onApply(filterConfig);
  };

  const handleSave = () => {
    if (onSave) onSave(filterConfig);
  };

  const handleCheckboxToggle = (
    conditionId,
    optionValue,
    currentValue,
    groupIndex,
  ) => {
    const currentValues = currentValue
      ? currentValue.split(",").map((v) => v.trim())
      : [];
    let newValues;
    if (currentValues.includes(optionValue)) {
      newValues = currentValues.filter((v) => v !== optionValue);
    } else {
      newValues = [...currentValues, optionValue];
    }
    updateCondition(conditionId, { value: newValues.join(",") }, groupIndex);
  };

  const isOptionSelected = (optionValue, currentValue) => {
    if (!currentValue) return false;
    return currentValue
      .split(",")
      .map((v) => v.trim())
      .includes(optionValue);
  };

  const hasValidConditions = () => {
    if (!filterConfig?.conditions || !Array.isArray(filterConfig.conditions))
      return false;
    if (filterConfig.conditions.length === 0) return false;
    return filterConfig.conditions.some((condition) => {
      if (condition.type === "group") {
        return (
          condition.conditions &&
          Array.isArray(condition.conditions) &&
          condition.conditions.length > 0 &&
          condition.conditions.some((c) => c.field && c.operator)
        );
      }
      return condition.field && condition.operator;
    });
  };

  const canApplyFilters = hasValidConditions();

  const renderCondition = (condition, index, groupIndex = null) => {
    const fieldInfo = getFieldInfo(condition.field);
    const availableOperators = fieldInfo ? OPERATORS[fieldInfo.type] || [] : [];

    const needsValue =
      condition.operator &&
      condition.operator !== "is_empty" &&
      condition.operator !== "is_not_empty" &&
      condition.operator !== "is_true" &&
      condition.operator !== "is_false";

    const showCheckboxes =
      (condition.operator === "in" || condition.operator === "not_in") &&
      (fieldInfo?.type === "select" || fieldInfo?.type === "user") &&
      fieldInfo?.options &&
      fieldInfo.options.length > 0;

    return (
      <div
        key={condition.id}
        className={`flex flex-wrap items-start gap-2 p-3 rounded-lg border ${
          isDarkMode
            ? "bg-gray-800/50 border-gray-700"
            : "bg-gray-50 border-gray-200"
        }`}
      >
        {/* Field Select */}
        <select
          value={condition.field}
          onChange={(e) =>
            updateCondition(condition.id, { field: e.target.value }, groupIndex)
          }
          className={`flex-1 min-w-[200px] ${selectCls}`}
        >
          <option value="">Select field...</option>
          {PROJECT_FIELDS.map((field) => (
            <option key={field.value} value={field.value}>
              {field.label}
            </option>
          ))}
        </select>

        {/* Operator Select */}
        {condition.field && (
          <select
            value={condition.operator}
            onChange={(e) =>
              updateCondition(
                condition.id,
                { operator: e.target.value },
                groupIndex,
              )
            }
            className={`flex-1 min-w-[180px] ${selectCls}`}
          >
            <option value="">Select operator...</option>
            {availableOperators.map((op) => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>
        )}

        {/* Value Input */}
        {needsValue && (
          <>
            {showCheckboxes ? (
              <div
                className={`flex-1 min-w-[300px] p-3 border rounded-lg ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600"
                    : "bg-white border-gray-300"
                }`}
              >
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {fieldInfo.options.map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-center gap-2 cursor-pointer p-2 rounded transition-colors ${
                        isDarkMode
                          ? "hover:bg-gray-600/50"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isOptionSelected(
                          option.value,
                          condition.value,
                        )}
                        onChange={() =>
                          handleCheckboxToggle(
                            condition.id,
                            option.value,
                            condition.value,
                            groupIndex,
                          )
                        }
                        className={`w-4 h-4 text-blue-600 rounded focus:ring-blue-500 focus:ring-2 ${
                          isDarkMode
                            ? "bg-gray-600 border-gray-500"
                            : "bg-white border-gray-300"
                        }`}
                      />
                      <span
                        className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {option.label}
                      </span>
                    </label>
                  ))}
                </div>
                <p
                  className={`text-xs mt-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {condition.value
                    ? `${condition.value.split(",").filter((v) => v.trim()).length} selected`
                    : "Select one or more options"}
                </p>
              </div>
            ) : fieldInfo?.type === "select" ? (
              <select
                value={condition.value}
                onChange={(e) =>
                  updateCondition(
                    condition.id,
                    { value: e.target.value },
                    groupIndex,
                  )
                }
                className={`flex-1 min-w-[200px] ${selectCls}`}
              >
                <option value="">Select value...</option>
                {fieldInfo.options?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : fieldInfo?.type === "user" ? (
              <select
                value={condition.value}
                onChange={(e) =>
                  updateCondition(
                    condition.id,
                    { value: e.target.value },
                    groupIndex,
                  )
                }
                className={`flex-1 min-w-[200px] ${selectCls}`}
              >
                <option value="">Select user...</option>
                {fieldInfo.options?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : condition.operator === "between" ? (
              <div className="flex flex-1 min-w-[200px] gap-2">
                <input
                  type={
                    fieldInfo?.type === "date"
                      ? "date"
                      : fieldInfo?.type === "number" ||
                          fieldInfo?.type === "decimal"
                        ? "number"
                        : "text"
                  }
                  value={
                    condition.value ? condition.value.split(",")[0] || "" : ""
                  }
                  onChange={(e) =>
                    updateCondition(
                      condition.id,
                      {
                        value: `${e.target.value},${
                          condition.value
                            ? condition.value.split(",")[1] || ""
                            : ""
                        }`,
                      },
                      groupIndex,
                    )
                  }
                  placeholder="From"
                  step={fieldInfo?.type === "decimal" ? "0.01" : undefined}
                  className={`flex-1 ${inputCls}`}
                />
                <input
                  type={
                    fieldInfo?.type === "date"
                      ? "date"
                      : fieldInfo?.type === "number" ||
                          fieldInfo?.type === "decimal"
                        ? "number"
                        : "text"
                  }
                  value={
                    condition.value ? condition.value.split(",")[1] || "" : ""
                  }
                  onChange={(e) =>
                    updateCondition(
                      condition.id,
                      {
                        value: `${
                          condition.value
                            ? condition.value.split(",")[0] || ""
                            : ""
                        },${e.target.value}`,
                      },
                      groupIndex,
                    )
                  }
                  placeholder="To"
                  step={fieldInfo?.type === "decimal" ? "0.01" : undefined}
                  className={`flex-1 ${inputCls}`}
                />
              </div>
            ) : condition.operator === "in_last" ||
              condition.operator === "in_next" ? (
              <input
                type="number"
                value={condition.value}
                onChange={(e) =>
                  updateCondition(
                    condition.id,
                    { value: e.target.value },
                    groupIndex,
                  )
                }
                placeholder="Number of days"
                className={`flex-1 min-w-[200px] ${inputCls}`}
              />
            ) : condition.operator === "contains_all" ||
              condition.operator === "in" ||
              condition.operator === "not_in" ? (
              <div className="flex-1 min-w-[200px]">
                <input
                  type="text"
                  value={condition.value}
                  onChange={(e) =>
                    updateCondition(
                      condition.id,
                      { value: e.target.value },
                      groupIndex,
                    )
                  }
                  placeholder="Enter values separated by commas"
                  className={`w-full ${inputCls}`}
                />
                <p
                  className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Separate multiple values with commas
                </p>
              </div>
            ) : (
              <input
                type={
                  fieldInfo?.type === "date"
                    ? "date"
                    : fieldInfo?.type === "number" ||
                        fieldInfo?.type === "decimal"
                      ? "number"
                      : "text"
                }
                value={condition.value}
                onChange={(e) =>
                  updateCondition(
                    condition.id,
                    { value: e.target.value },
                    groupIndex,
                  )
                }
                placeholder="Enter value..."
                step={fieldInfo?.type === "decimal" ? "0.01" : undefined}
                className={`flex-1 min-w-[200px] ${inputCls}`}
              />
            )}
          </>
        )}

        {/* Remove Button */}
        <button
          onClick={() => removeCondition(condition.id, groupIndex)}
          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-lg transition-colors"
          title="Remove condition"
        >
          <Trash2 size={16} />
        </button>
      </div>
    );
  };

  const renderConditionGroup = (group, groupIndex) => {
    return (
      <div
        key={group.id}
        className={`p-4 rounded-lg border-2 border-dashed ${
          isDarkMode
            ? "bg-gray-800/30 border-gray-600"
            : "bg-gray-50 border-gray-300"
        }`}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Match
            </span>
            <select
              value={group.logic}
              onChange={(e) => updateGroupLogic(groupIndex, e.target.value)}
              className={`px-3 py-1 rounded text-sm focus:outline-none focus:border-blue-500 ${
                isDarkMode
                  ? "bg-gray-700 border border-gray-600 text-white"
                  : "bg-white border border-gray-300 text-gray-900"
              }`}
            >
              <option value="AND">All</option>
              <option value="OR">Any</option>
            </select>
            <span
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              of these conditions:
            </span>
          </div>

          <button
            onClick={() => {
              const newConfig = {
                ...filterConfig,
                conditions: filterConfig.conditions.filter(
                  (_, i) => i !== groupIndex,
                ),
              };
              setFilterConfig(newConfig);
            }}
            className="text-red-400 hover:text-red-300 text-sm"
          >
            Remove Group
          </button>
        </div>

        <div className="space-y-2">
          {group.conditions.map((condition, index) =>
            renderCondition(condition, index, groupIndex),
          )}
        </div>

        <button
          onClick={() => addCondition(groupIndex)}
          className="mt-3 flex items-center gap-2 px-3 py-2 text-blue-400 hover:text-blue-300 text-sm"
        >
          <Plus size={16} />
          Add Condition
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {filterConfig.conditions.length > 0 && (
        <div
          className={`flex items-center gap-3 p-3 rounded-lg border ${
            isDarkMode
              ? "bg-gray-800/50 border-gray-700"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <span
            className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
          >
            Match
          </span>
          <select
            value={filterConfig.logic}
            onChange={(e) => updateRootLogic(e.target.value)}
            className={`px-3 py-1.5 rounded-lg text-sm focus:outline-none focus:border-blue-500 ${
              isDarkMode
                ? "bg-gray-700 border border-gray-600 text-white"
                : "bg-white border border-gray-300 text-gray-900"
            }`}
          >
            <option value="AND">All</option>
            <option value="OR">Any</option>
          </select>
          <span
            className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
          >
            of the following:
          </span>
        </div>
      )}

      <div className="space-y-3">
        {filterConfig.conditions.map((item, index) => {
          if (item.type === "group") return renderConditionGroup(item, index);
          return renderCondition(item, index);
        })}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => addCondition()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
        >
          <Plus size={16} />
          Add Condition
        </button>

        <button
          onClick={addConditionGroup}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm ${
            isDarkMode
              ? "bg-gray-700 hover:bg-gray-600 text-white"
              : "bg-gray-200 hover:bg-gray-300 text-gray-700"
          }`}
        >
          <Plus size={16} />
          Add Condition Group
        </button>
      </div>

      <div
        className={`flex justify-end gap-2 pt-4 border-t ${
          isDarkMode ? "border-gray-700" : "border-gray-200"
        }`}
      >
        {showSaveButton && (
          <button
            onClick={handleSave}
            disabled={!canApplyFilters}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:cursor-not-allowed ${
              isDarkMode
                ? "bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white"
                : "bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 text-gray-700"
            }`}
          >
            <Save size={16} />
            Save Filter
          </button>
        )}

        <button
          onClick={handleApply}
          disabled={!canApplyFilters}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <Filter size={16} />
          Apply Filters
        </button>
      </div>

      {filterConfig.conditions.length > 0 && (
        <div
          className={`p-3 rounded-lg border ${
            isDarkMode
              ? "bg-gray-800/30 border-gray-700"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <div
            className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            Active Filters: {filterConfig.conditions.length} condition
            {filterConfig.conditions.length !== 1 ? "s" : ""}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedProjectFilterBuilder;
